import { useEffect } from "react";
import { useContent } from "../context/ContentContext";

// Convert #RRGGBB to "H S% L%" channel string for Tailwind hsl(var / alpha).
export function hexToHslChannels(hex) {
  if (!hex) return null;
  let h = hex.replace("#", "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  const r = parseInt(h.substring(0, 2), 16) / 255;
  const g = parseInt(h.substring(2, 4), 16) / 255;
  const b = parseInt(h.substring(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let hue = 0;
  let sat = 0;
  const light = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    sat = light > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        hue = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        hue = (b - r) / d + 2;
        break;
      default:
        hue = (r - g) / d + 4;
    }
    hue /= 6;
  }
  return `${Math.round(hue * 360)} ${Math.round(sat * 100)}% ${Math.round(light * 100)}%`;
}

const VAR_MAP = {
  void: "--c-void",
  surface: "--c-surface",
  gold: "--c-gold",
  coral: "--c-coral",
  ink: "--c-ink",
  smoke: "--c-smoke",
};

export function loadFonts(heading, body) {
  const families = [];
  if (heading) families.push(heading.trim().replace(/ /g, "+") + ":ital,wght@0,400;0,500;0,600;0,700;0,800;0,900;1,400;1,600");
  if (body && body !== heading) families.push(body.trim().replace(/ /g, "+") + ":wght@200;300;400;500;600;700");
  const href = `https://fonts.googleapis.com/css2?${families.map((f) => `family=${f}`).join("&")}&display=swap`;
  let link = document.getElementById("dynamic-fonts");
  if (!link) {
    link = document.createElement("link");
    link.id = "dynamic-fonts";
    link.rel = "stylesheet";
    document.head.appendChild(link);
  }
  link.href = href;
}

// Applies a theme object (colors + fonts) to the document root.
export function applyTheme(theme) {
  if (!theme) return;
  const root = document.documentElement;
  if (theme.colors) {
    Object.entries(VAR_MAP).forEach(([key, cssVar]) => {
      const channels = hexToHslChannels(theme.colors[key]);
      if (channels) root.style.setProperty(cssVar, channels);
    });
  }
  if (theme.fonts) {
    const { heading, body } = theme.fonts;
    if (heading) root.style.setProperty("--font-heading", `'${heading}', serif`);
    if (body) root.style.setProperty("--font-body", `'${body}', sans-serif`);
    loadFonts(heading, body);
  }
}

export const ThemeProvider = ({ children }) => {
  const { content } = useContent();

  useEffect(() => {
    applyTheme(content?.theme);
  }, [content]);

  return children;
};
